import { ChevronLeft as ArrowBackIosIcon, ChevronRight as ArrowForwardIosIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { KRUMAGO } from "../../s3-assets/Krumago.jsx";
import { getAllProductCategories, getAllProducts } from "../../../api/action/products.jsx";
import ScrollableFeaturedProducts from "./components/ScrollableFeaturedProducts.jsx";
import { academyLogger } from "utils/logger";

const TEAL_FLOW_IMAGES = [KRUMAGO.IMAGES.BANNER_1, KRUMAGO.IMAGES.BANNER_2];

function TealFlowTemplate() {
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const companyPrimayId = useSelector((state) => state.companyReducer.company?.primaryId);
  const user = useSelector((state) => state?.auth?.data);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!companyPrimayId) {
        setCategoryProducts([]);
        return;
      }

      try {
        const response = await getAllProducts(companyPrimayId)(dispatch);
        setCategoryProducts(Array.isArray(response) ? response : []);
      } catch (error) {
        academyLogger.error("Error fetching products:", error);
      }
    };
    fetchProducts();
  }, [companyPrimayId, dispatch]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!companyPrimayId) {
        setCategories([]);
        return;
      }

      try {
        const response = await getAllProductCategories(companyPrimayId)(dispatch);
        setCategories(Array.isArray(response) ? response : []);
      } catch (error) {
        academyLogger.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [companyPrimayId, dispatch]);

  const images = TEAL_FLOW_IMAGES;

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  useEffect(() => {
    const interval = setInterval(nextImage, 3000);
    return () => clearInterval(interval);
  }, [nextImage]);

  return (
    <div className="px-2 sm:px-4 lg:px-8 bg-gradient-to-b from-teal-50 to-white">
      {/* Carousel Section */}
      <div className="carousel-container relative overflow-hidden rounded-lg mb-6 sm:mb-8 shadow-lg">
        <div
          className="carousel-inner flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
        >
          {images.map((src, index) => (
            <img
              loading="lazy"
              key={index}
              src={src}
              alt={`Carousel ${index}`}
              className="w-full h-auto flex-shrink-0"
              style={{
                height: "700px",
                width: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          ))}
        </div>
        <button
          className="absolute top-1/2 transform -translate-y-1/2 left-2 bg-teal-600 text-white p-2 rounded-full shadow-lg hover:bg-teal-700 transition duration-200"
          onClick={prevImage}
        >
          <ArrowBackIosIcon fontSize="medium" />
        </button>
        <button
          className="absolute top-1/2 transform -translate-y-1/2 right-2 bg-teal-600 text-white p-2 rounded-full shadow-lg hover:bg-teal-700 transition duration-200"
          onClick={nextImage}
        >
          <ArrowForwardIosIcon fontSize="medium" />
        </button>
      </div>

      {/* Shop By Category Section */}
      <div className="text-center px-2 sm:px-4 lg:px-8 py-8">
        {/* <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-teal-900">
          Shop By Category
        </h2> */}
        <h2 className="text-4xl sm:text-5xl font-bold mb-8 text-center text-teal-900">
          Shop By Category
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
          {categories.map((category, index) => (
            <div
              key={index}
              className="relative overflow-hidden rounded-lg transition-transform transform hover:scale-105 cursor-pointer shadow-md hover:shadow-lg"
              onClick={() =>
                navigate(`/category/tf/${category.name}`, {
                  state: { name: category.name },
                })
              }
            >
              <img
                loading="lazy"
                src={category.imageURL || KRUMAGO.IMAGES.ELE}
                alt={category.name}
                className="w-full h-48 object-cover rounded-lg"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity duration-300 ease-in-out rounded-lg">
                <h3 className="text-lg sm:text-xl font-bold text-white text-center">
                  {category.name}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable Featured Products Section */}
      <ScrollableFeaturedProducts
        productList={categoryProducts}
        navigate={navigate}
        loggedIn={!!user}
      />
    </div>
  );
}

export default TealFlowTemplate;
